/* eslint-disable complexity */
import React, {Component} from 'react';

import {
	TEST_STEPS,
	TEST_STEPS_BY_ID,
	YOUTH_TEST_STEPS_BY_ID,
	CHILD_TEST_STEPS_BY_ID,
	CHILD_SCRNG_TEST_STEPS_BY_ID,
	CHILD_SCRNG_DLD_TEST_STEP_IDS,
	YOUTH_TEST_NORMING_STEP_IDS,
	Z_SCORE_OFFSET,
	Z_SCORE_MAX,
} from 'constants/index';
import strings from 'utils/strings';
import roundWithDecimals from 'utils/roundWithDecimals';
import {getPronoun} from 'utils/getPronouns';
import {getTimeTakenInSeconds} from '../Utils/getTimeTakenInSecondsForLeseproven';

const FOLLOWUP_DLD_TEST_STEP_IDS = {
	ARBEIDSMINNE: 0,
	HURTIGBENEVNING: 3,
	ETTMINUTTSTESTEN: 4,
	SPOONERISM: 5,
	FONEMUTELATELSE: 8,
	SETNINGSMINNE: 10,
	NONORDREPETISJON: 13,
};

class Subtest extends Component {
	constructor(props) {
		super(props);

		this.state = {};
	}

	formatToMinutes(seconds) {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		const formattedSeconds = remainingSeconds.toString().padStart(2, '0');
		return strings.formatString(
			strings.minutesColonSeconds,
			minutes,
			formattedSeconds
		);
	}

	getDetailedReportScoreLevel(zScore, testReportLang) {
		const score = Number(zScore);

		if (isNaN(score)) {
			return '';
		}

		const isNynorsk = testReportLang === 'nn';

		if (score <= -2) {
			return isNynorsk ? 'betydeleg under' : 'betydelig under';
		}

		if (score <= -1) {
			return 'klart under';
		}

		if (score < 1) {
			return isNynorsk ? 'innanfor' : 'innenfor';
		}

		if (score < 2) {
			return 'klart over';
		}

		return isNynorsk ? 'betydeleg over' : 'betydelig over';
	}

	formatDetailedReportScore(value) {
		if (value === null || typeof value === 'undefined' || value === '') {
			return '–';
		}

		const numericValue = Number(value);

		if (isNaN(numericValue)) {
			return '–';
		}

		return roundWithDecimals(numericValue, 2);
	}

	formatDetailedReportCompactScore(value) {
		if (value === null || typeof value === 'undefined' || value === '') {
			return '–';
		}

		const numericValue = Number(value);

		if (isNaN(numericValue)) {
			return '–';
		}

		return String(Number(roundWithDecimals(numericValue, 2)));
	}

	formatDetailedReportDuration(durationMs) {
		if (
			durationMs === null ||
			typeof durationMs === 'undefined' ||
			durationMs === ''
		) {
			return '–';
		}

		const duration = Number(durationMs);

		if (isNaN(duration) || duration < 0) {
			return '–';
		}

		const totalSeconds = Math.round(duration / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;

		return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
	}

	renderNewScreeningSubtestReport({
		subtest,
		testResult,
		testReportLang,
		ageGroup,
		type,
	}) {
		const {id, totalCorrect, zScore, eScore, answers} = subtest;
		const isChild = type === 'child-screening';
		const isYouth = type === 'youth-screening';
		const isAdult = type === 'adult-screening';

		const numericGrade = Number(ageGroup);

		const localizedStrings =
			testReportLang === 'nn' ? strings._props.nn : strings;

		const orddiktatMaxScore = isChild
			? numericGrade >= 2 && numericGrade <= 4
				? 20
				: numericGrade >= 5 && numericGrade <= 7
					? 25
					: '–'
			: isYouth
				? 30
				: isAdult
					? 34
					: '–';

		const leseprovenMaxScore = isChild
			? numericGrade >= 2 && numericGrade <= 4
				? 8
				: numericGrade >= 5 && numericGrade <= 6
					? 7
					: numericGrade === 7
						? 8
						: '–'
			: isYouth
				? 10
				: isAdult
					? 13
					: '–';
		const ordforstaelseMaxScore = isChild
			? numericGrade >= 2 && numericGrade <= 4
				? 16
				: numericGrade >= 5 && numericGrade <= 7
					? 17
					: '–'
			: isYouth
				? 19
				: isAdult
					? 25
					: '–';

		const ids = isChild
			? {
					orddiktat: CHILD_SCRNG_DLD_TEST_STEP_IDS.ORDDIKTAT,
					ordkjedetesten:
						CHILD_SCRNG_DLD_TEST_STEP_IDS.ORDKJEDETESTEN,
					byttelyd: CHILD_SCRNG_DLD_TEST_STEP_IDS.BYTTELYDTESTEN,
					skrivehastighet:
						CHILD_SCRNG_DLD_TEST_STEP_IDS.SKRIVEHASTIGHET,
					leseproven: CHILD_SCRNG_DLD_TEST_STEP_IDS['LESEPRØVEN'],
					ordforstaelse:
						CHILD_SCRNG_DLD_TEST_STEP_IDS['ORDFORSTÅELSE'],
					setningsforstaelse:
						CHILD_SCRNG_DLD_TEST_STEP_IDS['SPRAKFORSTÅELSE'],
				}
			: {
					orddiktat: YOUTH_TEST_NORMING_STEP_IDS.ORDDIKTAT,
					ordkjedetesten: YOUTH_TEST_NORMING_STEP_IDS.ORDKJEDETESTEN,
					skrivehastighet:
						YOUTH_TEST_NORMING_STEP_IDS.SKRIVEHASTIGHET,
					leseproven: YOUTH_TEST_NORMING_STEP_IDS['LESEPRØVEN'],
					ordforstaelse: YOUTH_TEST_NORMING_STEP_IDS['ORDFORSTÅELSE'],
					setningsforstaelse:
						YOUTH_TEST_NORMING_STEP_IDS['SPRAKFORSTÅELSE'],
					tulleord: YOUTH_TEST_NORMING_STEP_IDS.TULLEORDTESTEN,
				};

		const reportConfigById = {
			[ids.orddiktat]: {
				heading: localizedStrings.detailedReportOrddiktatHeading,

				description: isAdult
					? localizedStrings.detailedReportOrddiktatAdultDescription
					: localizedStrings.detailedReportOrddiktatDescription,

				maxScore: orddiktatMaxScore,

				normText: isAdult
					? localizedStrings.detailedReportAdultOrddiktatNormText
					: localizedStrings.detailedReportOrddiktatNormText,
			},
			[ids.ordkjedetesten]: {
				heading: localizedStrings.detailedReportOrdkjedetestenHeading,

				description: isAdult
					? localizedStrings.detailedReportOrdkjedetestenAdultDescription
					: localizedStrings.detailedReportOrdkjedetestenChildYouthDescription,

				maxScore: null,

				normText: isAdult
					? localizedStrings.detailedReportOrdkjedetestenAdultNormText
					: isChild && numericGrade >= 2 && numericGrade <= 4
						? localizedStrings.detailedReportOrdkjedetestenSmallChildNormText
						: localizedStrings.detailedReportOrdkjedetestenChildYouthNormText,
			},
			[ids.ordforstaelse]: {
				heading: localizedStrings.detailedReportOrdforstaelseHeading,

				description: isAdult
					? localizedStrings.detailedReportOrdforstaelseAdultDescription
					: isChild && numericGrade >= 2 && numericGrade <= 4
						? localizedStrings.detailedReportOrdforstaelseSmallChildDescription
						: localizedStrings.detailedReportOrdforstaelseChildYouthDescription,

				maxScore: ordforstaelseMaxScore,

				normText: isAdult
					? localizedStrings.detailedReportAdultNormText
					: localizedStrings.detailedReportChildYouthNormText,
			},
			[ids.skrivehastighet]: {
				heading: localizedStrings.detailedReportSkrivehastighetHeading,

				description: isAdult
					? localizedStrings.detailedReportSkrivehastighetAdultDescription
					: localizedStrings.detailedReportSkrivehastighetChildYouthDescription,

				maxScore: null,

				normText: isAdult
					? localizedStrings.detailedReportSkrivehastighetAdultNormText
					: localizedStrings.detailedReportSkrivehastighetChildYouthNormText,

				resultText: isAdult
					? localizedStrings.detailedReportSkrivehastighetAdultResultText
					: localizedStrings.detailedReportSkrivehastighetChildYouthResultText,
			},

			...(isChild
				? {
						[ids.byttelyd]: {
							heading:
								localizedStrings.detailedReportByttelydHeading,
							description:
								localizedStrings.detailedReportByttelydDescription,
							maxScore: 21,
							normText:
								localizedStrings.detailedReportChildYouthNormText,
						},
					}
				: {}),

			[ids.leseproven]: {
				heading: localizedStrings.detailedReportLeseprovenHeading,

				description: isAdult
					? localizedStrings.detailedReportLeseprovenAdultDescription
					: localizedStrings.detailedReportLeseprovenDescription,

				maxScore: leseprovenMaxScore,

				normText: isAdult
					? localizedStrings.detailedReportAdultNormText
					: localizedStrings.detailedReportChildYouthNormText,
			},
			...(!isChild
				? {
						[ids.tulleord]: {
							heading:
								localizedStrings.detailedReportTulleordtestenHeading,

							description: isAdult
								? localizedStrings.detailedReportTulleordtestenAdultDescription
								: localizedStrings.detailedReportTulleordtestenYouthDescription,

							maxScore: 25,

							normText: isAdult
								? localizedStrings.detailedReportAdultNormText
								: localizedStrings.detailedReportChildYouthNormText,
						},
					}
				: {}),
			[ids.setningsforstaelse]: {
				heading:
					localizedStrings.detailedReportSetningsforstaelseHeading,

				description: isAdult
					? localizedStrings.detailedReportSetningsforstaelseAdultDescription
					: localizedStrings.detailedReportSetningsforstaelseChildYouthDescription,

				maxScore: 18,

				normText: isAdult
					? localizedStrings.detailedReportAdultNormText
					: localizedStrings.detailedReportChildYouthNormText,
			},
		};
		const reportConfig = reportConfigById[id];

		if (!reportConfig) {
			return null;
		}

		const testUser = testResult.testUser || {};
		const isTestOwner = testResult.isTestOwner;

		const hiddenName = localizedStrings.hiddenname || strings.hiddenname;

		const fullName =
			isTestOwner && testUser.firstName
				? `${testUser.firstName} ${testUser.lastName || ''}`.trim()
				: hiddenName;

		const firstName =
			isTestOwner && testUser.firstName ? testUser.firstName : hiddenName;

		const gradeWord = localizedStrings.grade || strings.grade;

		const normGroupLabel = isAdult
			? localizedStrings.detailedReportNormGroup
			: `${ageGroup}.${gradeWord}`;
		const scoreLevel = this.getDetailedReportScoreLevel(
			zScore,
			testReportLang
		);

		/*
		 * Keep eScore in the normalized report data.
		 * It is fetched from the DB now, but the department
		 * has not requested it in the visible score block yet.
		 */
		const normText = strings.formatString(
			reportConfig.normText,
			reportConfig.maxScore,
			normGroupLabel,
			this.formatDetailedReportCompactScore(eScore)
		);

		const resultTextTemplate =
			reportConfig.resultText ||
			(isAdult
				? localizedStrings.detailedReportAdultResultText
				: localizedStrings.detailedReportChildYouthResultText);

		const resultText = strings.formatString(
			resultTextTemplate,
			fullName,
			this.formatDetailedReportCompactScore(totalCorrect),
			this.formatDetailedReportScore(zScore),
			firstName,
			scoreLevel
		);

		const leseprovenTimeTakenInSeconds =
			id === ids.leseproven ? getTimeTakenInSeconds(answers) : null;
		const extraFormattedVariableTextForLeseproven =
			id === ids.leseproven && leseprovenTimeTakenInSeconds
				? strings.formatString(
						localizedStrings.reportLeseprovenExtraVariableText,
						this.formatToMinutes(leseprovenTimeTakenInSeconds)
					)
				: '';

		return (
			<div className="sub-test-Report sub-test-Report--detailed" key={id}>
				<h3 className="sub-test-Report__name">
					{reportConfig.heading}
				</h3>

				<div className="sub-test-report__scores">
					<div className="sub-test-Report">
						<p>{reportConfig.description}</p>

						<p>{normText}</p>

						<p>
							{resultText}{' '}
							{extraFormattedVariableTextForLeseproven}
						</p>

						<div className="sub-test-Report__margin" />
					</div>
				</div>
			</div>
		);
	}

	// eslint-disable-next-line complexity
	renderNewFollowupSubtestReport({
		subtest,
		testResult,
		testReportLang,
		ageGroup,
		testType,
	}) {
		const {id, totalCorrect, zScore, eScore, vars} = subtest;

		const isAdult = testType === 'adult-followup';
		const isChild = testType === 'child-followup';
		const numericGrade = Number(ageGroup);

		const localizedStrings =
			testReportLang === 'nn' ? strings._props.nn : strings;
		const hurtigbenevningQuestionCount =
			isChild && numericGrade >= 2 && numericGrade <= 4 ? 25 : 50;

		let hurtigbenevningVars = {};

		if (vars) {
			try {
				hurtigbenevningVars =
					typeof vars === 'string' ? JSON.parse(vars) : vars;
			} catch (error) {
				hurtigbenevningVars = {};
			}
		}
		const hasHurtigbenevningDuration =
			hurtigbenevningVars.duration !== null &&
			typeof hurtigbenevningVars.duration !== 'undefined' &&
			hurtigbenevningVars.duration !== '';
		const hurtigbenevningDurationMs = hasHurtigbenevningDuration
			? Number(hurtigbenevningVars.duration)
			: null;

		const hurtigbenevningDurationSeconds =
			hurtigbenevningDurationMs !== null &&
			!isNaN(hurtigbenevningDurationMs) &&
			hurtigbenevningDurationMs > 0
				? hurtigbenevningDurationMs / 1000
				: null;

		const hurtigbenevningCorrect =
			hurtigbenevningDurationSeconds !== null &&
			!isNaN(Number(totalCorrect))
				? Math.round(
						Number(totalCorrect) * hurtigbenevningDurationSeconds
					)
				: null;

		const hurtigbenevningRawScore = totalCorrect;

		const hurtigbenevningDuration = this.formatDetailedReportDuration(
			hurtigbenevningDurationMs
		);

		const reportConfigById = {
			[FOLLOWUP_DLD_TEST_STEP_IDS.ARBEIDSMINNE]: {
				heading: localizedStrings.detailedReportArbeidsminneHeading,

				description: isAdult
					? localizedStrings.detailedReportArbeidsminneAdultDescription
					: localizedStrings.detailedReportArbeidsminneChildYouthDescription,

				normText: isAdult
					? localizedStrings.detailedReportArbeidsminneAdultNormText
					: localizedStrings.detailedReportArbeidsminneChildYouthNormText,
			},
			[FOLLOWUP_DLD_TEST_STEP_IDS.HURTIGBENEVNING]: {
				heading: localizedStrings.detailedReportHurtigbenevningHeading,

				description: isAdult
					? localizedStrings.detailedReportHurtigbenevningAdultDescription
					: localizedStrings.detailedReportHurtigbenevningChildYouthDescription,

				normText: isAdult
					? localizedStrings.detailedReportHurtigbenevningAdultNormText
					: isChild && numericGrade >= 2 && numericGrade <= 4
						? localizedStrings.detailedReportHurtigbenevningSmallChildNormText
						: localizedStrings.detailedReportHurtigbenevningChildYouthNormText,

				rawScore: hurtigbenevningRawScore,

				resultText: isAdult
					? localizedStrings.detailedReportHurtigbenevningAdultResultText
					: localizedStrings.detailedReportHurtigbenevningChildYouthResultText,

				correctCount: hurtigbenevningCorrect,
				duration: hurtigbenevningDuration,
			},
			[FOLLOWUP_DLD_TEST_STEP_IDS.ETTMINUTTSTESTEN]: {
				heading: localizedStrings.detailedReportEttminuttstestenHeading,

				description: isAdult
					? localizedStrings.detailedReportEttminuttstestenAdultDescription
					: localizedStrings.detailedReportEttminuttstestenChildYouthDescription,

				normText: isAdult
					? localizedStrings.detailedReportEttminuttstestenAdultNormText
					: localizedStrings.detailedReportEttminuttstestenChildYouthNormText,

				resultText: isAdult
					? localizedStrings.detailedReportEttminuttstestenAdultResultText
					: localizedStrings.detailedReportEttminuttstestenChildYouthResultText,
			},
			[FOLLOWUP_DLD_TEST_STEP_IDS.NONORDREPETISJON]: {
				heading: localizedStrings.detailedReportNonordrepetisjonHeading,

				description: isAdult
					? localizedStrings.detailedReportNonordrepetisjonAdultDescription
					: localizedStrings.detailedReportNonordrepetisjonChildYouthDescription,

				normText: isAdult
					? localizedStrings.detailedReportNonordrepetisjonAdultNormText
					: isChild
						? localizedStrings.detailedReportNonordrepetisjonChildNormText
						: localizedStrings.detailedReportNonordrepetisjonYouthNormText,
			},
			[FOLLOWUP_DLD_TEST_STEP_IDS.SETNINGSMINNE]: {
				heading: localizedStrings.detailedReportSetningsminneHeading,

				description: isAdult
					? localizedStrings.detailedReportSetningsminneAdultDescription
					: localizedStrings.detailedReportSetningsminneChildYouthDescription,

				normText: isAdult
					? localizedStrings.detailedReportSetningsminneAdultNormText
					: isChild
						? localizedStrings.detailedReportSetningsminneChildNormText
						: localizedStrings.detailedReportSetningsminneYouthNormText,
			},
			...(!isChild || (numericGrade >= 6 && numericGrade <= 7)
				? {
						[FOLLOWUP_DLD_TEST_STEP_IDS.SPOONERISM]: {
							heading:
								localizedStrings.detailedReportSpoonerismHeading,

							description: isAdult
								? localizedStrings.detailedReportSpoonerismAdultDescription
								: localizedStrings.detailedReportSpoonerismChildYouthDescription,

							normText: isAdult
								? localizedStrings.detailedReportSpoonerismAdultNormText
								: isChild
									? localizedStrings.detailedReportSpoonerismChildNormText
									: localizedStrings.detailedReportSpoonerismYouthNormText,
						},
					}
				: {}),
			...(isChild && numericGrade >= 2 && numericGrade <= 5
				? {
						[FOLLOWUP_DLD_TEST_STEP_IDS.FONEMUTELATELSE]: {
							heading:
								localizedStrings.detailedReportFonemutelatelseHeading,

							description:
								localizedStrings.detailedReportFonemutelatelseDescription,

							normText:
								localizedStrings.detailedReportFonemutelatelseNormText,
						},
					}
				: {}),
		};

		const reportConfig = reportConfigById[id];

		if (!reportConfig) {
			return null;
		}
		const reportRawScore =
			typeof reportConfig.rawScore !== 'undefined'
				? reportConfig.rawScore
				: totalCorrect;
		const testUser = testResult.testUser || {};
		const isTestOwner = testResult.isTestOwner;

		const hiddenName = localizedStrings.hiddenname || strings.hiddenname;

		const fullName =
			isTestOwner && testUser.firstName
				? `${testUser.firstName} ${testUser.lastName || ''}`.trim()
				: hiddenName;

		const firstName =
			isTestOwner && testUser.firstName ? testUser.firstName : hiddenName;

		const gradeWord = localizedStrings.grade || strings.grade;

		const normGroupLabel = isAdult
			? localizedStrings.detailedReportNormGroup
			: `${ageGroup}.${gradeWord}`;

		const scoreLevel = this.getDetailedReportScoreLevel(
			zScore,
			testReportLang
		);

		const normText = strings.formatString(
			reportConfig.normText,
			normGroupLabel,
			this.formatDetailedReportCompactScore(eScore)
		);

		let resultText;

		if (id === FOLLOWUP_DLD_TEST_STEP_IDS.HURTIGBENEVNING) {
			resultText = strings.formatString(
				reportConfig.resultText,
				fullName,
				reportConfig.correctCount !== null
					? String(reportConfig.correctCount)
					: '–',
				reportConfig.duration,
				this.formatDetailedReportCompactScore(reportRawScore),
				this.formatDetailedReportScore(zScore),
				firstName,
				scoreLevel
			);
		} else {
			const resultTextTemplate =
				reportConfig.resultText ||
				(isAdult
					? localizedStrings.detailedReportAdultResultText
					: localizedStrings.detailedReportChildYouthResultText);

			resultText = strings.formatString(
				resultTextTemplate,
				fullName,
				this.formatDetailedReportCompactScore(totalCorrect),
				this.formatDetailedReportScore(zScore),
				firstName,
				scoreLevel
			);
		}

		return (
			<div className="sub-test-Report sub-test-Report--detailed" key={id}>
				<h3 className="sub-test-Report__name">
					{reportConfig.heading}
				</h3>

				<div className="sub-test-report__scores">
					<div className="sub-test-Report">
						<p>{reportConfig.description}</p>

						<p>{normText}</p>

						<p>{resultText}</p>

						<div className="sub-test-Report__margin" />
					</div>
				</div>
			</div>
		);
	}

	// eslint-disable-next-line complexity
	render() {
		const {
			subtest,
			type,
			testResult,
			testType,
			subtest: {language},
			testReportLang,
		} = this.props;
		console.log('Subtest obejct:  ', subtest);

		const {testUser} = testResult;
		const testUserPronoun = getPronoun(testUser.gender, language);
		const classInfo =
			// eslint-disable-next-line no-nested-ternary
			type === 'child'
				? testUser.class || testUser.ageGroup
				: type === 'child-screening' || type === 'youth-screening'
					? testUser.education.youthGrade
					: testUser.education.garde ||
						testUser.class ||
						strings.theagegroup;
		const ageGroup =
			classInfo === 'yrkesfag' ||
			classInfo === 'bachelor' ||
			classInfo === 'master' ||
			classInfo === 'phd' ||
			classInfo === 'other' ||
			classInfo === 'studiespesialisering'
				? 'adult'
				: classInfo;
		const isTestOwner = testResult.isTestOwner;

		let completeName = strings.hiddenname;
		let onlyFirstName = strings.hiddenname;
		if (testUser.firstName) {
			completeName = `${testUser.firstName} ${testUser.lastName}`;
			onlyFirstName = testUser.firstName;
		}
		const {
			totalCorrect,
			id,
			zScore,
			percentile,
			tScore,
			scaledScore,
			vars,
			answers,
		} = subtest;

		let leseprovenTimeTakenInSeconds = null;

		if (id === 5) {
			leseprovenTimeTakenInSeconds = getTimeTakenInSeconds(answers);
		}

		const zScoreImageLabelWidth = 20.79;
		const zScoreImageRightPaddingWidth = 2.25;
		const zScoreChartWidth =
			100 - zScoreImageRightPaddingWidth - zScoreImageLabelWidth;

		let subtestInfo = null;

		switch (type) {
			case 'child':
				subtestInfo = CHILD_TEST_STEPS_BY_ID[id];
				break;
			case 'adult-screening':
				subtestInfo = TEST_STEPS_BY_ID[id];
				break;
			case 'youth-screening':
				subtestInfo = YOUTH_TEST_STEPS_BY_ID[id];
				break;
			case 'child-screening':
				subtestInfo = CHILD_SCRNG_TEST_STEPS_BY_ID[id];
				break;
		}
		const isDldScreening = [
			'child-screening',
			'youth-screening',
			'adult-screening',
		].includes(type);

		const childDetailedReportIds = [
			CHILD_SCRNG_DLD_TEST_STEP_IDS.ORDDIKTAT,
			CHILD_SCRNG_DLD_TEST_STEP_IDS.ORDKJEDETESTEN,
			CHILD_SCRNG_DLD_TEST_STEP_IDS.BYTTELYDTESTEN,
			CHILD_SCRNG_DLD_TEST_STEP_IDS.SKRIVEHASTIGHET,
			CHILD_SCRNG_DLD_TEST_STEP_IDS['LESEPRØVEN'],
			CHILD_SCRNG_DLD_TEST_STEP_IDS['ORDFORSTÅELSE'],
			CHILD_SCRNG_DLD_TEST_STEP_IDS['SPRAKFORSTÅELSE'],
		];

		const youthAdultDetailedReportIds = [
			YOUTH_TEST_NORMING_STEP_IDS.ORDDIKTAT,
			YOUTH_TEST_NORMING_STEP_IDS.ORDKJEDETESTEN,
			YOUTH_TEST_NORMING_STEP_IDS.SKRIVEHASTIGHET,
			YOUTH_TEST_NORMING_STEP_IDS['LESEPRØVEN'],
			YOUTH_TEST_NORMING_STEP_IDS.TULLEORDTESTEN,
			YOUTH_TEST_NORMING_STEP_IDS['ORDFORSTÅELSE'],
			YOUTH_TEST_NORMING_STEP_IDS['SPRAKFORSTÅELSE'],
		];

		const useNewScreeningReport =
			isDldScreening &&
			(type === 'child-screening'
				? childDetailedReportIds.includes(id)
				: youthAdultDetailedReportIds.includes(id));

		if (useNewScreeningReport) {
			return this.renderNewScreeningSubtestReport({
				subtest,
				testResult,
				testReportLang,
				ageGroup,
				type,
			});
		}

		const isDldFollowup = [
			'child-followup',
			'youth-followup',
			'adult-followup',
		].includes(testType);

		const isFonemutelatelseReport =
			testType === 'child-followup' &&
			Number(ageGroup) >= 2 &&
			Number(ageGroup) <= 5 &&
			id === FOLLOWUP_DLD_TEST_STEP_IDS.FONEMUTELATELSE;
		const isSpoonerismReport =
			id === FOLLOWUP_DLD_TEST_STEP_IDS.SPOONERISM &&
			(testType === 'youth-followup' ||
				testType === 'adult-followup' ||
				(testType === 'child-followup' &&
					Number(ageGroup) >= 6 &&
					Number(ageGroup) <= 7));

		const useNewFollowupReport =
			isDldFollowup &&
			([
				FOLLOWUP_DLD_TEST_STEP_IDS.ARBEIDSMINNE,
				FOLLOWUP_DLD_TEST_STEP_IDS.HURTIGBENEVNING,
				FOLLOWUP_DLD_TEST_STEP_IDS.ETTMINUTTSTESTEN,
				FOLLOWUP_DLD_TEST_STEP_IDS.SETNINGSMINNE,
				FOLLOWUP_DLD_TEST_STEP_IDS.NONORDREPETISJON,
			].includes(id) ||
				isFonemutelatelseReport ||
				isSpoonerismReport);

		if (useNewFollowupReport) {
			return this.renderNewFollowupSubtestReport({
				subtest,
				testResult,
				testReportLang,
				ageGroup,
				testType,
			});
		}

		const subtestQuestionCount = subtestInfo.questionCount;
		const subtestLabel = subtestInfo.label;
		const subTestReportHeading =
			testReportLang === 'nn'
				? subtestInfo.reportHeadingNynorsk
				: subtestInfo.reportHeading;
		const subTestReportExplanation =
			testReportLang === 'nn'
				? subtestInfo.reportExplainationNynorsk
				: subtestInfo.reportExplaination;
		const subTestReportBHExplanation =
			testReportLang === 'nn'
				? subtestInfo.reportBHexplainationNynorsk
				: subtestInfo.reportBHexplaination;
		const subTestReportVariableText =
			testReportLang === 'nn'
				? subtestInfo.reportVariableTextNynorsk
				: subtestInfo.reportVariableText;
		const subTestReportLeseprovenExtraVariableText =
			testReportLang === 'nn'
				? strings._props.nn.reportLeseprovenExtraVariableText
				: strings.reportLeseprovenExtraVariableText;
		const subTestReportBHVariableText =
			testReportLang === 'nn'
				? subtestInfo.reportVariableTextBHNynorsk
				: subtestInfo.reportVariableTextBH;
		const notAvailable =
			testReportLang === 'nn'
				? strings._props.nn.notavailable
				: strings.notavailable;
		let totalCorrectLabel;
		let formattedVariableText;
		const extraFormattedVariableTextForLeseproven =
			id === 5 && leseprovenTimeTakenInSeconds
				? strings.formatString(
						subTestReportLeseprovenExtraVariableText,
						this.formatToMinutes(leseprovenTimeTakenInSeconds)
					)
				: '';
		let subtestExplanation = '';
		let meanScore;
		let standardDeviation;

		if (type === 'child') {
			let myVars;
			if (vars) {
				myVars = JSON.parse(vars);
			}

			switch (id) {
				case 0: {
					totalCorrectLabel = `${totalCorrect}  rette`;
					meanScore = subtestInfo.norms[ageGroup]
						? subtestInfo.norms[ageGroup].average
						: notAvailable;
					standardDeviation = subtestInfo.norms[ageGroup]
						? subtestInfo.norms[ageGroup].standardDeviation
						: notAvailable;

					formattedVariableText = strings.formatString(
						subTestReportVariableText,
						`${isTestOwner ? onlyFirstName : strings.hiddenname}`,
						`${totalCorrect}`,
						`${roundWithDecimals(zScore, 2)}`,
						`${testUserPronoun}`
					);
					subtestExplanation = strings.formatString(
						subTestReportExplanation,
						<b>{`${
							type !== 'child-screening' &&
							type !== 'child-screening'
								? strings.thisagegroup
								: `${classInfo}.${strings.grade}`
						}`}</b>,
						<b>{meanScore}</b>,
						<b>{standardDeviation}</b>
					);
					break;
				}
				case 3: {
					let totalErrors = 0;
					let duration = 0;
					meanScore = subtestInfo.norms[ageGroup]
						? subtestInfo.norms[ageGroup].average
						: notAvailable;
					standardDeviation = subtestInfo.norms[ageGroup]
						? subtestInfo.norms[ageGroup].standardDeviation
						: notAvailable;
					subtestExplanation = strings.formatString(
						ageGroup == 1 || ageGroup == 0
							? subTestReportBHExplanation
							: subTestReportExplanation,
						<b>{`${
							type !== 'child-screening' &&
							type !== 'child-screening'
								? /* ageGroup + strings.class */ strings.thisagegroup
								: `${classInfo}.${strings.grade}`
						}`}</b>,
						<b>{meanScore}</b>,
						<b>{standardDeviation}</b>
					);
					formattedVariableText = strings.formatString(
						subTestReportVariableText,
						`${isTestOwner ? onlyFirstName : strings.hiddenname}`,
						`${roundWithDecimals(totalCorrect, 2)}`,
						`${roundWithDecimals(zScore, 2)}`,
						`${testUserPronoun}`
					);
					if (myVars && myVars.duration) {
						duration = myVars.duration;
						totalErrors = myVars.totalErrors;
					}
					totalCorrectLabel = strings.formatString(
						strings.timeUsedSecondsAndErrorsLiteral,
						Math.round(duration / 1000),
						totalErrors
					);
					break;
				}
				case 4: {
					totalCorrectLabel = `${totalCorrect}  ${strings.wordreadcorrectly}`;
					meanScore = subtestInfo.norms[ageGroup]
						? subtestInfo.norms[ageGroup].average
						: notAvailable;
					standardDeviation = subtestInfo.norms[ageGroup]
						? subtestInfo.norms[ageGroup].standardDeviation
						: notAvailable;
					subtestExplanation = strings.formatString(
						subTestReportExplanation,
						<b>{`${
							type !== 'child-screening' &&
							type !== 'child-screening'
								? /* ageGroup + strings.class */ strings.thisagegroup
								: `${classInfo}.${strings.grade}`
						}`}</b>,
						<b>{meanScore}</b>,
						<b>{standardDeviation}</b>
					);
					formattedVariableText = strings.formatString(
						subTestReportVariableText,
						`${isTestOwner ? onlyFirstName : strings.hiddenname}`,
						`${totalCorrect}`,
						`${roundWithDecimals(zScore, 2)}`,
						`${testUserPronoun}`
					);

					break;
				}
				case 6: {
					totalCorrectLabel = `${totalCorrect}  ${subtestQuestionCount}`;
					meanScore = subtestInfo.norms[ageGroup]
						? subtestInfo.norms[ageGroup].average
						: notAvailable;
					standardDeviation = subtestInfo.norms[ageGroup]
						? subtestInfo.norms[ageGroup].standardDeviation
						: notAvailable;
					subtestExplanation = strings.formatString(
						subTestReportExplanation,
						<b>{`${
							type !== 'child-screening' &&
							type !== 'child-screening'
								? /* ageGroup + strings.class */ strings.thisagegroup
								: `${classInfo}.${strings.grade}`
						}`}</b>,
						<b>{meanScore}</b>,
						<b>{standardDeviation}</b>
					);
					formattedVariableText =
						ageGroup == 0
							? strings.formatString(
									subTestReportBHVariableText,
									`${
										isTestOwner
											? onlyFirstName
											: strings.hiddenname
									}`,
									`${totalCorrect}`,
									`${
										isTestOwner
											? onlyFirstName
											: strings.hiddenname
									}`,
									`${testUserPronoun}`
								)
							: strings.formatString(
									subTestReportVariableText,
									`${
										isTestOwner
											? onlyFirstName
											: strings.hiddenname
									}`,
									`${totalCorrect}`,
									`${roundWithDecimals(zScore, 2)}`,
									`${testUserPronoun}`
								);

					break;
				}
				default: {
					totalCorrectLabel = `${totalCorrect} av ${subtestQuestionCount}`;
					meanScore = subtestInfo.norms[ageGroup]
						? subtestInfo.norms[ageGroup].average
						: notAvailable;
					standardDeviation = subtestInfo.norms[ageGroup]
						? subtestInfo.norms[ageGroup].standardDeviation
						: notAvailable;
					subtestExplanation = strings.formatString(
						subTestReportExplanation,
						<b>{`${
							type !== 'child-screening' &&
							type !== 'child-screening'
								? /* ageGroup + strings.class */ strings.thisagegroup
								: `${classInfo}.${strings.grade}`
						}`}</b>,
						<b>{meanScore}</b>,
						<b>{standardDeviation}</b>
					);

					formattedVariableText = strings.formatString(
						subTestReportVariableText,
						`${isTestOwner ? onlyFirstName : strings.hiddenname}`,
						`${roundWithDecimals(totalCorrect, 2)}`,
						`${roundWithDecimals(zScore, 2)}`,
						`${testUserPronoun}`
					);
				}
			}
		} else if (type !== 'child') {
			if (subtestInfo.name === TEST_STEPS.SKRIVEHASTIGHET) {
				totalCorrectLabel = `${totalCorrect} ${strings.wordsperminute}`;
				meanScore = subtestInfo.norms[ageGroup]
					? subtestInfo.norms[ageGroup].average
					: notAvailable;
				if (meanScore === notAvailable && type === 'adult-screening') {
					meanScore = subtestInfo.norms['default'].average;
				}
				standardDeviation = subtestInfo.norms[ageGroup]
					? subtestInfo.norms[ageGroup].standardDeviation
					: notAvailable;
				if (
					standardDeviation === notAvailable &&
					type === 'adult-screening'
				) {
					standardDeviation =
						subtestInfo.norms['default'].standardDeviation;
				}
				subtestExplanation = strings.formatString(
					subTestReportExplanation,
					<b>{`${
						type !== 'child-screening' && type !== 'child-screening'
							? /* ageGroup + strings.class */ strings.thisagegroup
							: `${classInfo}.${strings.grade}`
					}`}</b>,
					<b>{meanScore}</b>,
					<b>{standardDeviation}</b>
				);
				formattedVariableText = strings.formatString(
					subTestReportVariableText,
					`${isTestOwner ? onlyFirstName : strings.hiddenname}`,
					`${totalCorrect}`,
					`${roundWithDecimals(zScore, 2)}`,
					`${testUserPronoun}`
				);
			} else {
				totalCorrectLabel = `${totalCorrect} av ${subtestQuestionCount}`;
				meanScore = subtestInfo.norms[ageGroup]
					? subtestInfo.norms[ageGroup].average
					: notAvailable;
				if (meanScore === notAvailable && type === 'adult-screening') {
					meanScore = subtestInfo.norms['default'].average;
				}
				standardDeviation = subtestInfo.norms[ageGroup]
					? subtestInfo.norms[ageGroup].standardDeviation
					: notAvailable;
				if (
					standardDeviation === notAvailable &&
					type === 'adult-screening'
				) {
					standardDeviation =
						subtestInfo.norms['default'].standardDeviation;
				}
				subtestExplanation = strings.formatString(
					subTestReportExplanation,
					<b>{`${
						type !== 'child-screening' && type !== 'child-screening'
							? /* ageGroup + strings.class */ strings.thisagegroup
							: `${classInfo}.${strings.grade}`
					}`}</b>,
					<b>{meanScore}</b>,
					<b>{standardDeviation}</b>
				);
				formattedVariableText = strings.formatString(
					subTestReportVariableText,
					`${isTestOwner ? onlyFirstName : strings.hiddenname}`,
					`${totalCorrect}`,
					`${roundWithDecimals(zScore, 2)}`,
					`${testUserPronoun}`
				);
			}
		}

		const testZScorePercentage = (zScore + Z_SCORE_OFFSET) / Z_SCORE_MAX;
		const testZChartBarLeft =
			zScoreImageLabelWidth + testZScorePercentage * zScoreChartWidth;

		return (
			<div className="sub-test-Report" key={id}>
				<h3 className="sub-test-Report__name">{`${subtestLabel}`}</h3>
				<div className="sub-test-report__scores">
					<div className="sub-test-Report">
						<p className="sub-test-Report__heading">
							{subTestReportHeading}
						</p>
						<p>{subtestExplanation}</p>

						<p>
							{formattedVariableText}
							<span>
								{zScore <= -1 && (
									<span className="lit-indented">
										<span className="lit-indented__under">
											{' '}
											{strings.under}{' '}
										</span>
										{strings.averageForGrade}
									</span>
								)}
								{zScore >= 1 && (
									<span className="lit-indented">
										<span className="lit-indented__over">
											{' '}
											{strings.over}{' '}
										</span>
										{strings.averageForGrade}
									</span>
								)}
								{zScore < 1 && zScore > -1 && ageGroup != 0 && (
									<span className="lit-indented">
										<span className="lit-indented__in">
											{' i '}
										</span>
										{strings.averageForGrade}
									</span>
								)}
								{zScore < 1 &&
									zScore > -1 &&
									ageGroup == 0 &&
									id == 6 && <span>{''}</span>}
							</span>{' '}
							{extraFormattedVariableTextForLeseproven}
						</p>
						<div className="sub-test-Report__margin"></div>
					</div>
					{this.props.children}
				</div>
			</div>
		);
	}
}

export default Subtest;
